// Commercial vocabulary of the web (ADR-0047). Amounts arrive as integer micro-USD from the server and
// are only FORMATTED here; the browser never computes credit, consumption or prices.
import { ISO_COUNTRY_CODES } from "@interactads/domain";
import { formatDateTime } from "@/lib/format";

export const usd = (micros: number | null | undefined) =>
  micros === null || micros === undefined
    ? "No disponible"
    : new Intl.NumberFormat("es-HN", { style: "currency", currency: "USD", currencyDisplay: "code" })
        .format(micros / 1_000_000)
        .replace("USD", "US$")
        .trim();

export const ORG_TYPE_LABELS: Record<string, string> = { AGENCY: "Agencia", DIRECT_CLIENT: "Cliente directo" };
export const COMPANY_STATUS: Record<string, { label: string; tone: "neutral" | "success" | "warning" | "danger" }> = {
  PENDING_REVIEW: { label: "Pendiente de validación", tone: "warning" },
  ACTIVE: { label: "Activa", tone: "success" },
  SUSPENDED: { label: "Suspendida", tone: "danger" },
  REJECTED: { label: "Requiere correcciones", tone: "danger" },
  NO_PROFILE: { label: "Sin perfil empresarial", tone: "neutral" },
};
export const PO_STATUS: Record<string, { label: string; tone: "neutral" | "success" | "warning" | "danger" }> = {
  PENDING_REVIEW: { label: "En revisión", tone: "warning" },
  APPROVED: { label: "Aprobada", tone: "success" },
  REJECTED: { label: "Rechazada", tone: "danger" },
  CANCELLED: { label: "Cancelada", tone: "neutral" },
};
export const AUTH_STATUS: Record<string, { label: string; tone: "neutral" | "success" | "warning" | "danger" }> = {
  SUBMITTED: { label: "En revisión", tone: "warning" },
  APPROVED: { label: "Autorizada", tone: "success" },
  REJECTED: { label: "Rechazada", tone: "danger" },
  SUPERSEDED: { label: "Reemplazada", tone: "neutral" },
  CANCELLED: { label: "Cancelada", tone: "neutral" },
};
export const PAYMENT_MODE_LABELS: Record<string, string> = {
  PREPAID: "Prepago",
  PURCHASE_ORDER: "Órdenes de compra",
  CREDIT: "Crédito",
};

/** Human text for COMMERCIAL_RULE problem codes (the server decides; this only explains). */
export const RULE_MESSAGES: Record<string, string> = {
  ALREADY_MEMBER: "Ya perteneces a una empresa. Pide acceso a su administrador o abre tu espacio de trabajo.",
  LEGAL_DATA_LOCKED:
    "Los datos legales de una empresa validada solo los cambia Media Interactiva. Escríbenos si necesitas corregirlos.",
  PURCHASE_ORDERS_DISABLED: "Las órdenes de compra aún no están habilitadas para tu empresa.",
  COMPANY_NOT_ACTIVE: "Tu empresa todavía no está validada por Media Interactiva.",
  DUPLICATE_NUMBER: "Ya registraste una orden con ese número.",
  PURCHASE_ORDER_NOT_APPROVED: "La orden de compra elegida todavía no está aprobada.",
  ALREADY_APPROVED: "Esta campaña ya tiene una autorización aprobada.",
  INVALID_REQUEST: "Revisa el presupuesto y las fechas: deben estar dentro del período de la orden.",
  INSUFFICIENT_CREDIT: "El monto supera el crédito disponible de la empresa.",
  INSUFFICIENT_PURCHASE_ORDER: "El monto supera el saldo sin asignar de la orden de compra.",
  INVALID_AMOUNT: "El monto aprobado no es válido para esta solicitud.",
  ALREADY_DECIDED: "Esta solicitud ya fue decidida.",
  REASON_REQUIRED: "Escribe el motivo.",
  DUPLICATE_TAX_ID:
    "Otra empresa registró la misma identificación tributaria. Confirma que la revisaste para activarla.",
  CREDIT_LIMIT_EXCEEDS_POLICY: "El límite máximo de crédito permitido es US$10,000.",
  INVALID_MANAGER: "El responsable debe ser un operador de Media Interactiva.",
  CPM_NOT_DEFINED: "Falta el CPM vigente de algún producto: no se puede calcular el consumo.",
  ACTIVE_CONTENT: "El PDF contiene contenido activo (scripts o acciones). Exporta la orden como PDF simple.",
  NOT_PDF: "El archivo no es un PDF válido.",
  ENCRYPTED: "El PDF está protegido con contraseña. Súbelo sin protección.",
  TOO_LARGE: "El archivo supera el tamaño permitido.",
  INVALID_FORMAT: "El formato de la identificación tributaria no corresponde al país.",
  REAL_CREDIT_DISABLED:
    "El crédito real está deshabilitado: solo empresas marcadas como datos de prueba pueden recibir crédito u órdenes aprobadas.",
  CREDIT_HOLD_ACTIVE: "La empresa está detenida por su crédito: registra un pago o libera el hold antes de aprobar.",
  CONSUMPTION_STALE:
    "Falta una lectura reciente del consumo de sus campañas. Usa «Medir consumo ahora» y vuelve a intentarlo.",
  PURCHASE_ORDER_CLOSED: "La orden de compra está cerrada: no financia nuevas campañas.",
  CONDITION_PERSISTS: "La condición que detuvo la entrega sigue vigente: el hold no se puede liberar todavía.",
  INVALID_POLICY: "Revisa los márgenes: el aviso debe quedar por debajo del corte.",
  ALREADY_CLOSED: "La orden de compra ya estaba cerrada.",
  NOT_APPROVED: "Solo se puede cerrar una orden aprobada.",
  REAL_BILLING_DISABLED:
    "La facturación real está deshabilitada: solo se registran facturas de empresas marcadas como datos de prueba.",
  ALREADY_INVOICED: "Alguna campaña ya está facturada en un período que se superpone con este.",
  INVALID_LINE: "Revisa las líneas: cada campaña aparece una sola vez, aprobada y con un importe positivo.",
  EXCEEDS_BALANCE: "El pago supera el saldo pendiente de la factura.",
  DUPLICATE_REFERENCE: "Ya registraste un pago con esa referencia y método.",
  HAS_PAYMENTS: "La factura tiene pagos registrados: anúlalos antes de anular la factura.",
  INVOICE_VOID: "La factura está anulada.",
  ALREADY_VOID: "Ya estaba anulada.",
  BILLING_PROFILE_INCOMPLETE:
    "Falta la dirección fiscal o el contacto de facturación de la empresa. Complétalos antes de activarla.",
};

export function ruleMessage(problem: { message: string; fields: readonly { path: string; problem: string }[] }) {
  const code = problem.fields[0]?.problem;
  return (code && RULE_MESSAGES[code]) || problem.message;
}

export const day = (value: string | null | undefined) => (value ? formatDateTime(value).split(",")[0] : "—");

/** Spanish name of an ISO 3166-1 country (the code itself when the browser has no name for it). */
export function countryName(code: string): string {
  try {
    return new Intl.DisplayNames(["es"], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Every ISO 3166-1 country (CA-O3: companies from any country), by Spanish name; Honduras first. */
export const COUNTRIES: ReadonlyArray<[string, string]> = (() => {
  const collator = new Intl.Collator("es");
  const all = ISO_COUNTRY_CODES.map((code): [string, string] => [code, countryName(code)]).sort((a, b) =>
    collator.compare(a[1], b[1])
  );
  return [...all.filter(([c]) => c === "HN"), ...all.filter(([c]) => c !== "HN")];
})();
export const TAX_LABEL: Record<string, string> = { HN: "RTN", US: "EIN", MX: "RFC" };

/** Navigation hints from the company view (null when the caller may not read it). */
export const commercialNav = (
  company: { terms: { purchaseOrdersEnabled: boolean }; canManage: boolean } | null | undefined
) => (company?.canManage ? { purchaseOrdersEnabled: company.terms.purchaseOrdersEnabled } : null);

/** A text field of a submitted form ("" when absent or a file). */
export const formField = (form: FormData, name: string): string => {
  const value = form.get(name);
  return typeof value === "string" ? value : "";
};
